/**
 * <as-gui-ctrls> — turnkey Paramate GUI-controls (transform gizmo) integration.
 *
 * Nest it inside <as-viewport>:
 *
 *   <as-viewport>
 *     <as-gui-ctrls model-matrix-param="main:workpieceModelMtx" mode="translate"></as-gui-ctrls>
 *   </as-viewport>
 *
 * The element owns a minimal Controls shim (see GizmoControlsShim below) and the
 * transform gizmo, so the host app never touches the Controls API and no Paramate
 * Controls library has to be loaded. The gizmo itself comes from a classic global
 * script the host page loads (same way as-configurator reads window.Paramate):
 *   - window.GUIControls.TransformGizmo   (/lib/GUIControls.js — the gizmo bundle)
 *
 * Gizmo commits go through the shared Paramate configurator, so undo/redo is picked
 * up by <as-configurator> automatically — one drag = one configure = one undo entry.
 * See docs/guiControls.md.
 *
 * Attributes:
 *   model-matrix-param  (required) trCAD param holding the workpiece instance model matrices
 *   world-matrix-param  (optional) trCAD param holding the default / world matrices
 *   mode                translate | rotate | scale   (default: translate)
 *   world-space         boolean flag — draw the gizmo in world space
 *   gizmo-size          number — gizmo handle size
 */

import { devlog } from '../../logic/utils.ts';

/**
 * Minimal Controls shim.
 *
 * The Paramate transform gizmo only needs three things from a "Controls" object:
 *   1. `.configurator`  — it reads params / setParamValue / debug through it.
 *   2. register / deregister so it is tracked as an "external control".
 *   3. `.updateControls()` — the configurator calls this after every
 *      configure/initialize/restore; it loops the external controls and calls
 *      `.update()` on each, which re-pulls the gizmo's matrix and keeps it in sync
 *      after an undo/redo re-applies the param.
 * The gizmo binds its OWN viewport pointer events, so no pointer routing is needed here.
 *
 * This mirrors only the essential surface of Paramate's real `Controls`
 * (lib-custom/paramateControls.js) without pulling that library in.
 *
 * NOTE / future: if we ever need the full Controls behaviour — HTML paramate-* inputs,
 * file-upload handling, ray-select, services, etc. — we can drop this shim and use the
 * real lib instead: `new (window as any).Paramate.Controls({ configurator })` (after
 * loading paramateControls.js). The gizmo wiring in _setup() is identical either way:
 * same constructor + registerExternalControl + updateControls contract. Keeping the shim
 * for now avoids the extra global dependency and keeps the app free of the Controls API.
 */
class GizmoControlsShim {
  configurator: any;
  externalControls: any[] = [];
  // Benign fields the real Controls exposes; some Paramate code paths read them.
  name = 'as-gui-ctrls';
  services: Record<string, unknown> = {};
  fileUploads: any[] = [];
  onBeforeUpdateControls: (() => boolean) | undefined;

  constructor(configurator: any) {
    this.configurator = configurator;
  }

  registerExternalControl(ctrl: any): void {
    if (!this.externalControls.includes(ctrl)) this.externalControls.push(ctrl);
  }

  deregisterExternalControl(ctrl: any): void {
    const i = this.externalControls.indexOf(ctrl);
    if (i !== -1) this.externalControls.splice(i, 1);
  }

  /** Called by the configurator after each configure — re-syncs every external control. */
  updateControls(): void {
    if (this.onBeforeUpdateControls && !this.onBeforeUpdateControls()) return;
    for (const ctrl of this.externalControls) ctrl.update?.();
  }
}

export class AsGuiCtrls extends HTMLElement {
  static tag = 'as-gui-ctrls';

  private _gizmo: any = null;
  private _controls: GizmoControlsShim | any = null;
  private _ownsControls = false;
  private _ready = false;

  static get observedAttributes() { return ['mode']; }

  /** The underlying gizmo instance (once set up), or null. */
  get gizmo() { return this._gizmo; }
  /** The Controls shim this element manages, or null. */
  get controls() { return this._controls; }

  connectedCallback() {
    this.style.display = 'none';
    devlog('[as-gui-ctrls] connected — waiting for configurator');
    void this._init();
  }

  disconnectedCallback() {
    this._teardown();
  }

  attributeChangedCallback(name: string, _old: string | null, val: string | null) {
    if (name === 'mode' && this._gizmo && val) this._gizmo.mode = val;
  }

  /** Switch the active gizmo mode. */
  setMode(mode: 'translate' | 'rotate' | 'scale') { this.setAttribute('mode', mode); }

  private async _init(): Promise<void> {
    const cfgEl = await this._findConfiguratorEl();
    if (!this.isConnected || this._ready) return;
    if (!cfgEl) {
      devlog('[as-gui-ctrls] no <as-configurator> element found');
      return;
    }
    const configurator = await cfgEl.waitForConfigurator();
    if (!this.isConnected || this._ready) return;
    devlog('[as-gui-ctrls] configurator received');
    this._whenViewport(configurator, (c) => this._setup(c));
  }

  /** Wait for the <as-configurator> element to exist and be upgraded (have waitForConfigurator). */
  private _findConfiguratorEl(): Promise<any> {
    const get = () => {
      const el = document.querySelector('as-configurator') as any;
      return el && typeof el.waitForConfigurator === 'function' ? el : null;
    };
    const found = get();
    if (found) return Promise.resolve(found);
    return new Promise((resolve) => {
      let tries = 0;
      const poll = setInterval(() => {
        const el = get();
        if (el || ++tries > 50 || !this.isConnected) { clearInterval(poll); resolve(el); }
      }, 60);
    });
  }

  /** The configurator can answer a tick before its viewPort is attached; wait for it. */
  private _whenViewport(configurator: any, cb: (c: any) => void): void {
    if (configurator.viewPort) { cb(configurator); return; }
    let tries = 0;
    const poll = setInterval(() => {
      if (this._ready || !this.isConnected) { clearInterval(poll); return; }
      if (configurator.viewPort) { clearInterval(poll); cb(configurator); }
      else if (++tries > 50) { clearInterval(poll); devlog('[as-gui-ctrls] configurator has no viewPort — cannot set up gizmo'); }
    }, 120);
  }

  private _setup(configurator: any): void {
    const GUIControls = (window as any).GUIControls;
    if (!GUIControls?.TransformGizmo) {
      devlog('[as-gui-ctrls] window.GUIControls not found — load /lib/GUIControls.js');
      return;
    }

    const modelParam = this.getAttribute('model-matrix-param');
    if (!modelParam) {
      devlog('[as-gui-ctrls] the model-matrix-param attribute is required');
      return;
    }
    const worldParam = this.getAttribute('world-matrix-param') || undefined;

    // Own a Controls shim so the app never has to — reuse one if already attached.
    if (configurator.controls) {
      this._controls = configurator.controls;
    } else {
      this._controls = new GizmoControlsShim(configurator);
      configurator.controls = this._controls; // the configurator drives updateControls() on this
      this._ownsControls = true;
    }

    const options: Record<string, unknown> = {
      hasChildren: false,
      worldSpace: this.hasAttribute('world-space'),
      scaleActive: false,
    };
    const sizeAttr = this.getAttribute('gizmo-size');
    if (sizeAttr) options.gizmoSize = Number(sizeAttr);

    this._gizmo = new GUIControls.TransformGizmo(
      configurator.viewPort,
      this._controls,
      options,
      modelParam,
      worldParam,
    );
    this._gizmo.mode = this.getAttribute('mode') || 'translate';
    this._gizmo.show();
    this._ready = true;
    devlog(`[as-gui-ctrls] gizmo ready (numGizmos=${this._gizmo.numGizmos})`);

    // A gizmo draws one handle-set per matrix in the model param. If the param
    // doesn't exist on the configurator (or is empty) there is nothing to draw —
    // surface that instead of a silently invisible gizmo.
    if (this._gizmo.numGizmos === 0) {
      devlog(
        `[as-gui-ctrls] gizmo has 0 instances — model-matrix-param "${modelParam}" is ` +
        `missing on the configurator or holds no matrices, so nothing will be drawn. ` +
        `Point it at a trCAD param that returns the serialized instance matrices (>= 1).`,
      );
    }

    this.dispatchEvent(new CustomEvent('as-gui-ctrls-ready', {
      bubbles: true,
      detail: { gizmo: this._gizmo, controls: this._controls },
    }));
  }

  private _teardown(): void {
    try { this._gizmo?.hide?.(); } catch { /* ignore */ }
    if (this._ownsControls && this._controls?.deregisterExternalControl && this._gizmo) {
      try { this._controls.deregisterExternalControl(this._gizmo); } catch { /* ignore */ }
    }
    this._gizmo = null;
    this._ready = false;
  }
}

if (!customElements.get(AsGuiCtrls.tag)) {
  customElements.define(AsGuiCtrls.tag, AsGuiCtrls);
}
